import { ApiTypes, getApiBase } from '@/services/helpers';

import { httpService } from '../httpService';
import type { GeneratedQuizQuestion, GenerateQuestionsServiceConfig } from './types/index';

const QUIZ_GENERATION_LANGUAGES = new Set(['en', 'de']);

function primaryLanguageSubtag(language: string): string {
  const trimmed = language.trim();
  if (trimmed === '') {
    return '';
  }
  const primary = trimmed.split('-')[0];
  return primary !== undefined && primary !== '' ? primary.toLowerCase() : '';
}

export function isQuizGenerationLanguageSupported(language: string): boolean {
  return QUIZ_GENERATION_LANGUAGES.has(primaryLanguageSubtag(language));
}

type QuizGenerationApiJson = {
  msg?: string;
  body?: unknown;
};

function isGeneratedQuizQuestion(value: unknown): value is GeneratedQuizQuestion {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const row = value as Record<string, unknown>;
  if (typeof row.question !== 'string' || Array.isArray(row.answers) === false) {
    return false;
  }
  return row.answers.every((ans: unknown) => {
    if (ans === null || typeof ans !== 'object') {
      return false;
    }
    const a = ans as Record<string, unknown>;
    return typeof a.answer === 'string' && typeof a.isCorrect === 'boolean';
  });
}

/**
 * Calls the NLP quiz generation service (same contract as poc-cms QuizGenerationClient.generateQuestions).
 * Uses `httpService` with `httpRequestConfig.baseUrl` set to the quiz-generation API when unset (non-OpenAPI).
 */
export async function generateQuestionsService({
  httpRequestConfig,
  params,
}: GenerateQuestionsServiceConfig): Promise<GeneratedQuizQuestion[]> {
  if (params === undefined) {
    throw new Error('Params are required');
  }

  const { body, signal } = params;

  if (isQuizGenerationLanguageSupported(body.language) === false) {
    throw new Error(`Language ${body.language} is not supported for quiz generation.`);
  }

  const client = httpService({
    ...httpRequestConfig,
    baseUrl:
      httpRequestConfig.baseUrl
      ?? getApiBase(ApiTypes.QUIZ_GENERATION, httpRequestConfig.target),
  });

  const json = await client.post<QuizGenerationApiJson>(
    '',
    { steps: body.steps, language: body.language },
    {
      credentials: 'omit',
      ...(signal !== undefined ? { signal } : {}),
    },
  );

  const responseBody = json.body;
  if (Array.isArray(responseBody) === false || responseBody.every(isGeneratedQuizQuestion) === false) {
    throw new Error('Quiz generation returned an invalid response.');
  }

  return responseBody;
}
